import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  MultiProgressBar,
  SemiCircleGuageChart,
  Spinner
} from '@components/index';
import { FEATURE_FLAGS } from '@constants/feature';
import { STORAGE_KEYS } from '@constants/storage';
import useCanAccessDashboardApis from '@features/dashboard/hooks/useCanAccessDashboardApis';
import { getLocalStorageItem } from '@utils/storage';

import {
  formatPaymentLinksData,
  getFormattedDateForChartAPI,
  getPaymentModeData
} from '../helper';
import { useLazyGetPaymentLinksAndModesQuery } from '../queries';
import { DateRangeType, PaymentDateFilterType } from '../types';

interface PaymentLinksAndModesProps {
  selectedDateRange: DateRangeType;
  selectedBranchId: string;
  selectedFilter: PaymentDateFilterType;
}

const PaymentLinksAndModes = ({
  selectedDateRange,
  selectedBranchId,
  selectedFilter
}: PaymentLinksAndModesProps) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const canAccessDashboardApis = useCanAccessDashboardApis();

  const isAutoDebitEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.AUTO_DEBIT
  );

  const [
    getPaymentLinksAndModes,
    { data: paymentLinksAndModesData, isLoading, isFetching, isUninitialized }
  ] = useLazyGetPaymentLinksAndModesQuery();

  const getPaymentLinksAndModesData = async () => {
    if (!canAccessDashboardApis) return;

    const { startDate, endDate } =
      getFormattedDateForChartAPI(selectedDateRange);

    await getPaymentLinksAndModes({
      organizationId,
      queryString: `?from_timestamp=${startDate}&to_timestamp=${endDate}&branch_id=${selectedBranchId}&time_filter_type=${selectedFilter}`
    });
  };

  const paymentLinkData = formatPaymentLinksData(paymentLinksAndModesData);
  const paymentModeData = getPaymentModeData(
    paymentLinksAndModesData,
    isAutoDebitEnabled
  );
  const isPaymentModesLoading =
    canAccessDashboardApis && (isLoading || isFetching || isUninitialized);

  useEffect(() => {
    getPaymentLinksAndModesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDateRange,
    selectedBranchId,
    selectedFilter,
    canAccessDashboardApis
  ]);

  return (
    <div className="flex flex-wrap gap-4 pb-4 xl:flex-nowrap">
      <div className="relative h-72 w-full rounded-2xl border border-cofeeNeutral4 bg-cofeeNeutral1 p-6 xl:w-1/2 2xl:w-2/5">
        <SemiCircleGuageChart
          title="Payment Links"
          className="w-full"
          chartClassName="w-full absolute top-10"
          chartValues={paymentLinkData}
        />
      </div>
      <div className=" w-full rounded-2xl border border-cofeeNeutral4 bg-cofeeNeutral1 p-6 xl:w-1/2 2xl:w-3/5">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-bold text-content">Payment Modes</h1>
          {/* <InfoIconTooltip
            id="payment-mode-chart"
            tooltipText="Payment Mode Tooltip"
          /> */}
        </div>
        <div className="flex h-full items-center justify-center">
          {isPaymentModesLoading ? (
            <div className="flex h-50 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <MultiProgressBar data={paymentModeData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentLinksAndModes;
